// Knowledge-base articles for /knowledge/{slug}.
//
// Every statement must be supportable by products.js specs or general,
// non-controversial building knowledge. Do NOT invent performance numbers,
// certifications, or claims. English + Estonian fields are required for
// published articles (both route families exist); Danish fields are added
// only after the /da launch.
//
// Fields:
//   slug                 URL slug under /knowledge/
//   title / titleEt      H1 + og:title
//   description / descriptionEt   meta description
//   datePublished / dateUpdated   'YYYY-MM-DD'
//   productSlugs         related products (linked at the bottom)
//   categoryPaths        related landing pages (English paths, linked)
//   sections             [{ heading, headingEt, paragraphs, paragraphsEt, list, listEt }]
//   published            boolean — only published:true entries render

export const articles = [
  {
    slug: 'outward-vs-inward-opening-exterior-doors',
    title: 'Outward- vs Inward-Opening Exterior Doors: How to Choose',
    titleEt: 'Välja- või sissepoole avanev välisuks: kuidas valida',
    description: 'Practical comparison of outward- and inward-opening wooden exterior doors — space, weather and hardware differences, with the SMARTLINE range as a reference.',
    descriptionEt: 'Praktiline võrdlus välja- ja sissepoole avanevate puidust välisuste vahel — ruumi-, ilmastiku- ja furnituurierinevused SMARTLINE seeria näitel.',
    datePublished: '2026-09-18',
    dateUpdated: '2026-09-18',
    published: true,
    productSlugs: ['smartline-standard', 'smartline-inswing'],
    categoryPaths: ['/outward-opening-exterior-doors', '/inward-opening-exterior-doors'],
    sections: [
      {
        heading: 'Two opening directions, one construction',
        headingEt: 'Kaks avanemissuunda, üks ehitus',
        paragraphs: [
          'Exterior doors open either outwards (away from the hallway) or inwards (into the hallway). Outward-opening entrance doors are the common choice in Nordic markets, while inward-opening doors remain familiar in many continental European buildings.',
          'In the Hestia SMARTLINE range both directions share the same core construction: a 60 mm door leaf with 50 mm EPS insulation, aluminium-sheet and HDF surfaces, and U-values of 0,77 for blind doors and 1,0 for glazed doors. The choice of direction therefore changes the frame, threshold and hardware layout — not the thermal performance.'
        ],
        paragraphsEt: [
          'Välisuksed avanevad kas väljapoole (esikust eemale) või sissepoole (esiku suunas). Väljapoole avanevad uksed on Põhjamaade turgudel tavaline valik, samas kui sissepoole avanevad uksed on paljudes Mandri-Euroopa hoonetes tavapärased.',
          'Hestia SMARTLINE seerias on mõlemal suunal sama põhiehitus: 60 mm ukselaba 50 mm EPS-isolatsiooniga, alumiiniumlehtede ja HDF-pindadega ning U-väärtusega 0,77 pimendatud ja 1,0 klaasitud ustele. Avanemissuuna valik muudab seega raami, läve ja furnituuri lahendust — mitte soojapidavust.'
        ]
      },
      {
        heading: 'When an outward-opening door makes sense',
        headingEt: 'Millal sobivad väljapoole avanevad uksed',
        list: [
          'The hallway is compact — the leaf does not swing into the entrance space.',
          'Wind-driven rain is a concern — wind load presses an outward-opening leaf against its seals.',
          'A deep exterior reveal or canopy protects the leaf and visitor while opening.',
          'The project specifies the Scandinavian-standard configuration.'
        ],
        listEt: [
          'Esik on väike — laba ei liigu sissepääsu ruumi.',
          'Tuulega kaasas tulev vihm on probleem — tuulekoormus surub väljapoole avaneva laba tihendite vastu.',
          'Sügav välimine palistus või varikatus kaitseb laba ja külastajat avamise ajal.',
          'Projekt eeldab Skandinaavia tavapärast lahendust.'
        ],
        paragraphs: [
          'Smartline is the outward-opening base product. It uses a 1-step pine frame (44 x 105 mm), an oak threshold (grey Accoya optional), lock-case 565 and two hinges. Single, double-door and sidelight configurations are available to order; project-specific dimensions and details are confirmed during quotation.'
        ],
        paragraphsEt: [
          'Smartline on väljapoole avanev põhitoode. Sellel on 1-astmeline männiraam (44 x 105 mm), tammelävi (valikuline hall Accoya), lukukorpus 565 ja kaks hinge. Ühepoolne, topeltukse ja küljevalgustiga lahendus on saadaval tellimisel; projektipõhised mõõdud ja detailid kinnitatakse pakkumise käigus.'
        ]
      },
      {
        heading: 'When an inward-opening door makes sense',
        headingEt: 'Millal sobivad sissepoole avanevad uksed',
        list: [
          'An outward swing is impractical — for example a narrow porch, steps directly outside, or a tight recess.',
          'The replacement must match an existing inward-opening frame and wall detail.',
          'A sheltered entrance means wind load on the leaf is not a design driver.'
        ],
        listEt: [
          'Väljapoole avanemine on ebapraktiline — näiteks kitsas veranda, astmed kohe ukse taga või madal süvend.',
          'Vahetus peab sobima olemasoleva sissepoole avaneva raami ja sõlmedetailiga.',
          'Kaitstud sissekäik tähendab, et tuulekoormus labale ei ole määrav.'
        ],
        paragraphs: [
          'Smartline Inward is the inward-opening model. It uses a 2-step pine frame (56 x 115 mm) with an aluminium threshold, lock-case 565 and two hinges — with the same 60 mm insulated leaf and glazing options as the rest of the range.'
        ],
        paragraphsEt: [
          'Smartline Inward on sissepoole avanev mudel. Sellel on 2-astmeline männiraam (56 x 115 mm), alumiiniumlävi, lukukorpus 565 ja kaks hinge — sama 60 mm soojustatud laba ja klaasvalikutega nagu kogu seerial.'
        ]
      },
      {
        heading: 'Practical checklist before ordering',
        headingEt: 'Praktiline kontrollnimekiri enne tellimust',
        list: [
          'Measure the wall opening and confirm the required size — the SMARTLINE grid is 9x20/21; 9,5x20,5; 10x20/21, with other sizes on request.',
          'Decide the opening direction early — frame, threshold and hinge layouts differ.',
          'Choose glazing: blind doors reach U-value 0,77; glazed versions are rated 1,0 with 2K clear glass.',
          'Specify the required lock, threshold and configuration (single, double-door or sidelight) for the project.'
        ],
        listEt: [
          'Mõõda seinaava ja kinnita vajalik suurus — SMARTLINE valik on 9x20/21; 9,5x20,5; 10x20/21, muud suurused päringu alusel.',
          'Otsusta avanemissuund varakult — raami, läve ja hingede lahendused erinevad.',
          'Vali klaasitus: pimendatud uste U-väärtus on 0,77; klaasitud versioonidel 1,0 koos 2K klaasiga.',
          'Määra projekti jaoks vajalik lukk, lävi ja konfiguratsioon (ühepoolne, topeltuks või küljevalgustiga uks).'
        ],
        paragraphs: [
          'For project-specific dimensions or hardware configurations, contact Hestia Doors — custom manufacturing is available across the range.'
        ],
        paragraphsEt: [
          'Projektipäraste mõõtude või furnituurikonfiguratsioonide jaoks võta ühendust Hestia Doorsiga — eritellimustootmine on saadaval kogu valikus.'
        ]
      }
    ]
  }
];
